import { z } from "zod";
import { CHANNEL_KEYS, type ChannelKey } from "./channels.js";
import type { OrderRowDto } from "./api-types.js";
import type { OrderStatusKey } from "./schemas/orders.js";
import type { TrendyolStatus } from "./catalog-trendyol.js";

/**
 * Metrik sözlüğü (docs/report-definitions.md):
 * - Ciro: iptal ve iade OLMAYAN siparişlerin KDV dahil ödenen toplamı (grandTotal).
 * - Sipariş: aynı kapsamdaki sipariş adedi. Ortalama sepet = ciro / sipariş.
 * - Gün sınırları İstanbul saatine (UTC+3) göredir.
 */
/** Ciroya girmeyen durumlar: iptal, tam iade, ödemesi alınmamış, teslim edilemeyen/sorunlu. */
export const REVENUE_EXCLUDED_STATUSES: readonly OrderStatusKey[] = ["cancelled", "returned", "awaiting_payment", "problem"];
/** Ödeme işlemi başarısız veya sürmekte olan siparişler ciroya girmez (IdeaSoft paymentStatus). */
export const REVENUE_EXCLUDED_PAYMENT_STATUSES = ["failed", "in_transaction"] as const;

export const DASHBOARD_RANGES = ["today", "7d", "30d", "90d", "year"] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];
export const DASHBOARD_RANGE_LABELS: Record<DashboardRange, string> = { today: "Bugün", "7d": "Son 7 gün", "30d": "Son 30 gün", "90d": "Son 90 gün", year: "Bu yıl" };

/** Genel Bakış ve Raporlarda kanal ayrımı: boş = tüm kanallar. */
export const channelFilterSchema = z.enum(CHANNEL_KEYS).optional();

export interface KpiBlock {
  /** Net ciro = brüt satış − onaylı geri ödemeler. */
  revenue: string;
  grossRevenue: string;
  refunds: string;
  orders: number;
  averageBasket: string;
  units: number;
  cancelledOrReturned: number;
}

export interface DashboardResponse {
  range: DashboardRange;
  channel: ChannelKey | null;
  current: KpiBlock;
  previous: KpiBlock;
  daily: { date: string; revenue: string; orders: number }[];
  statusCounts: Partial<Record<OrderStatusKey, number>>;
  attention: { key: "awaiting_approval" | "awaiting_payment" | "to_ship" | "return_requests" | "payment_failed" | "out_of_stock" | "sync_error"; count: number; label: string; href: string }[];
  recentOrders: OrderRowDto[];
  topProducts: { name: string; sku: string | null; units: number; revenue: string; imageUrl: string | null }[];
  /** Afiş görseli: dönemin en çok satan ürününün (yoksa en değerli ürünün) fotoğrafı. */
  heroImageUrl: string | null;
  /** ordersSyncedAt: siparişlerin kanaldan en son alındığı an (panodaki rakamların güncelliği). */
  channels: { name: string; key: ChannelKey; mode: "demo" | "live"; state: string; isPaused: boolean; lastSuccessfulSyncAt: string | null; ordersSyncedAt: string | null }[];
}

export const PRODUCT_SORTS = ["updated_desc", "name_asc", "brand_asc", "brand_desc", "price_asc", "price_desc", "stock_desc", "stock_asc", "created_desc"] as const;
export type ProductSort = (typeof PRODUCT_SORTS)[number];
export const PRODUCT_SORT_LABELS: Record<ProductSort, string> = {
  updated_desc: "Son güncellenen",
  created_desc: "En yeni eklenen",
  brand_asc: "Markaya göre (A-Z)",
  brand_desc: "Markaya göre (Z-A)",
  name_asc: "Ada göre (A-Z)",
  price_desc: "Fiyat (yüksekten düşüğe)",
  price_asc: "Fiyat (düşükten yükseğe)",
  stock_desc: "Stok (çoktan aza)",
  stock_asc: "Stok (azdan çoğa)",
};

/** Marka listesi (filtre seçenekleri). */
export interface BrandOption {
  name: string;
  products: number;
}
export const NO_BRAND = "__none__";

/** Çoklu marka: ?brand=A&brand=B (tek değer de kabul edilir). */
export const brandListSchema = z
  .preprocess((v) => (v === undefined || v === "" ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().trim().min(1).max(120)).max(200))
  .optional();

export const productsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(25),
  q: z.string().trim().max(100).optional(),
  stock: z.enum(["all", "out", "low", "in"]).default("all"),
  status: z.enum(["all", "active", "passive"]).default("all"),
  /** Marka adları; "__none__" = markasız ürünler. */
  brand: brandListSchema,
  channel: z.enum(CHANNEL_KEYS).optional(),
  /** Bu kanalda listelenmeyen ürünler (ör. Trendyol'a açılmamış). */
  notOnChannel: z.enum(CHANNEL_KEYS).optional(),
  sort: z.enum(PRODUCT_SORTS).default("updated_desc"),
  /** Merkezi katalog: içerik durumu (gönderilmedi / kanalda farklı / gönderilemedi). */
  content: z.enum(["all", "dirty", "drifted", "failed"]).default("all"),
  /** Trendyol'a ürün açma durumu (v1.2). */
  trendyol: z.enum(["all", "draft", "pending", "review", "rejected", "failed"]).default("all"),
});
export type ProductsQuery = z.infer<typeof productsQuerySchema>;

export interface ProductRowDto {
  id: string;
  name: string;
  brand: string | null;
  imageUrl: string | null;
  status: "active" | "passive" | "archived";
  variantCount: number;
  sku: string;
  listPrice: string | null;
  salePrice: string | null;
  stock: number;
  channels: { name: string; key: ChannelKey; stock: number | null; isActive: boolean; lastPulledAt: string | null }[];
  updatedAt: string;
  /** Merkezi katalog içerik durumu (IdeaSoft); içerik henüz panelde yönetilmiyorsa null. */
  contentStatus: "synced" | "dirty" | "drifted" | "failed" | null;
  /** Trendyol içerik durumu (taslak, onay bekliyor, reddedildi …); panelden hiç hazırlanmadıysa null. */
  trendyolStatus: TrendyolStatus | null;
}

export interface ProductsListResponse {
  items: ProductRowDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: { all: number; out: number; low: number; in: number };
  /** İçerik durumu sayıları (stok süzgecinden bağımsız). */
  contentCounts: { dirty: number; drifted: number; failed: number };
  trendyolCounts: { draft: number; pending: number; review: number; rejected: number; failed: number };
  lowStockThreshold: number;
  brands: BrandOption[];
}

export const salesReportQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  groupBy: z.enum(["day", "month", "product", "brand", "channel", "status", "external"]).default("day"),
  channel: z.enum(CHANNEL_KEYS).optional(),
});
export type SalesReportQuery = z.infer<typeof salesReportQuerySchema>;

export interface SalesReportResponse {
  query: SalesReportQuery;
  /** revenue = net (brüt − iade); durum kırılımlarında brüt. */
  rows: { key: string; label: string; orders: number; units: number; revenue: string; gross: string; refunds: string }[];
  totals: { orders: number; units: number; revenue: string; grossRevenue: string; refunds: string; averageBasket: string; cancelledOrReturned: number };
  definitions: string[];
}

export interface NotificationDto {
  id: string;
  type: string;
  severity: "info" | "warning" | "critical";
  title: string;
  body: string | null;
  link: string | null;
  status: "unread" | "read" | "snoozed" | "closed";
  createdAt: string;
  /** Bildirimin geldiği satış kanalı (kanaldan bağımsızsa null). */
  channel: ChannelKey | null;
}

export interface NotificationsResponse {
  items: NotificationDto[];
  unread: number;
}

/** Ürün düzenleme: varyant × kanal kayıtları (stok ve fiyat buradan kanala yazılır). */
export interface ProductListingsDto {
  productId: string;
  name: string;
  brand: string | null;
  listings: {
    id: string;
    variantTitle: string | null;
    sku: string;
    barcode: string | null;
    channel: { key: ChannelKey; name: string };
    stockQuantity: number | null;
    listPrice: string | null;
    salePrice: string | null;
    isActive: boolean;
    lastPulledAt: string | null;
    lastPushedAt: string | null;
  }[];
}

export const listingUpdateSchema = z
  .object({
    quantity: z.coerce.number().int().min(0).max(20_000).optional(),
    listPrice: z.string().regex(/^\d+(\.\d{1,2})?$/, "Fiyatı 1234.50 biçiminde girin").optional(),
    salePrice: z.string().regex(/^\d+(\.\d{1,2})?$/, "Fiyatı 1234.50 biçiminde girin").optional(),
    /** IdeaSoft: indirimi kaldır (satış fiyatı = liste fiyatı). */
    removeDiscount: z.boolean().optional(),
  })
  .refine((v) => v.quantity !== undefined || v.listPrice !== undefined || v.salePrice !== undefined || v.removeDiscount, "Değiştirilecek bir alan girin")
  .refine((v) => !v.listPrice || !v.salePrice || Number(v.salePrice) <= Number(v.listPrice), { message: "Satış fiyatı liste fiyatından büyük olamaz", path: ["salePrice"] });
export type ListingUpdateInput = z.infer<typeof listingUpdateSchema>;

export const returnDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]) });

export interface OperationResultDto {
  state: "accepted" | "verified" | "partial" | "failed";
  message: string | null;
}

/** IdeaSoft toplu fiyat / indirim işlemi. */
export const BULK_PRICE_ACTIONS = ["discount_percent", "discount_amount", "remove_discount", "list_percent"] as const;
export type BulkPriceAction = (typeof BULK_PRICE_ACTIONS)[number];
export const BULK_PRICE_ACTION_LABELS: Record<BulkPriceAction, string> = {
  discount_percent: "Yüzde indirim uygula",
  discount_amount: "Tutar indirimi uygula",
  remove_discount: "İndirimi kaldır",
  list_percent: "Liste fiyatını yüzde değiştir",
};
export const bulkPriceSchema = z
  .object({
    /** Seçili ürünler veya ürün listesindeki filtrenin tamamı. */
    productIds: z.array(z.string().max(40)).min(1).max(500).optional(),
    filter: z.record(z.string(), z.union([z.string(), z.array(z.string())])).optional(),
    action: z.enum(BULK_PRICE_ACTIONS),
    /** discount_percent: 0,01–90 · discount_amount: TL · list_percent: −50…+200 */
    value: z.coerce.number().optional(),
    /** Satış fiyatını tam liraya yuvarla (aşağı). */
    roundWhole: z.boolean().default(false),
    /** true: yalnız önizleme, kanala yazılmaz. */
    dryRun: z.boolean().default(true),
  })
  .refine((v) => Boolean(v.productIds?.length) !== Boolean(v.filter), "Ürün seçin veya filtre gönderin")
  .refine((v) => v.action === "remove_discount" || (typeof v.value === "number" && Number.isFinite(v.value)), { message: "Değer girin", path: ["value"] })
  .refine((v) => v.action !== "discount_percent" || ((v.value ?? 0) > 0 && (v.value ?? 0) <= 90), { message: "İndirim %0,01 ile %90 arasında olmalı", path: ["value"] })
  .refine((v) => v.action !== "discount_amount" || (v.value ?? 0) > 0, { message: "İndirim tutarı sıfırdan büyük olmalı", path: ["value"] })
  .refine((v) => v.action !== "list_percent" || ((v.value ?? 0) >= -50 && (v.value ?? 0) <= 200 && v.value !== 0), { message: "Değişim −%50 ile +%200 arasında olmalı", path: ["value"] });
export type BulkPriceInput = z.input<typeof bulkPriceSchema>;

export interface BulkPriceRowDto {
  listingId: string;
  productId: string;
  name: string;
  brand: string | null;
  sku: string | null;
  listBefore: string;
  saleBefore: string;
  listAfter: string;
  saleAfter: string;
}
export interface BulkPricePreviewDto {
  total: number;
  changed: number;
  skipped: number;
  sample: BulkPriceRowDto[];
  jobId: string | null;
}
export interface BulkPriceJobDto {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "dead";
  action: BulkPriceAction;
  total: number;
  done: number;
  ok: number;
  failed: number;
  errors: { name: string; message: string }[];
  createdAt: string;
  createdBy: string | null;
}

/** Kanallar arası ürün eşleştirme. */
export interface MatchSideDto {
  variantId: string;
  productId: string;
  listingId: string;
  name: string;
  variantTitle: string | null;
  brand: string | null;
  sku: string;
  barcode: string | null;
  imageUrl: string | null;
  price: string | null;
  stock: number | null;
  isActive: boolean;
}
/** Elle eşleştirme araması: IdeaSoft ürünü zaten bir Trendyol kaydına bağlıysa o kayıt. */
export interface MatchSearchItemDto extends MatchSideDto {
  linkedTrendyol: { sku: string; barcode: string | null; price: string | null; stock: number | null } | null;
}
/** trendyol / onlyTrendyol / unmatchedTrendyol / linkedTrendyol: seçili pazar yeri tarafı (alan adları geriye uyum için). */
export interface MatchCandidateDto {
  channel?: ChannelKey;
  trendyol: MatchSideDto;
  options: (MatchSideDto & { reason: "barcode" | "code" | "model" })[];
  confidence: "high" | "medium" | "low";
}
export interface MatchingResponse {
  /** Eşleştirilen pazar yeri ve IdeaSoft ile eşleştirilebilecek bağlı pazar yerleri. */
  channel: ChannelKey;
  channels: ChannelKey[];
  counts: { linked: number; onlyIdeasoft: number; onlyTrendyol: number };
  candidates: MatchCandidateDto[];
  /** Aday bulunamayan Trendyol ürünleri (elle eşleştirme). */
  unmatchedTrendyol: MatchSideDto[];
  settings: MatchingSettings;
}
export const mergeSchema = z.object({ pairs: z.array(z.object({ from: z.string().max(40), into: z.string().max(40) })).min(1).max(200) });
export const MATCH_LEVELS = ["high", "medium", "low"] as const;
export type MatchLevel = (typeof MATCH_LEVELS)[number];
/** Tümünü eşleştir: seçilen güven düzeylerindeki öneriler, satırda seçilen (yoksa ilk) IdeaSoft ürünüyle. */
export const mergeAllSchema = z.object({
  channel: z.enum(CHANNEL_KEYS).optional(),
  levels: z.array(z.enum(MATCH_LEVELS)).min(1).max(3).default(["high", "medium", "low"]),
  picks: z.record(z.string().max(40), z.string().max(40)).default({}),
});
export interface MergeAllResult {
  merged: number;
  /** Aynı IdeaSoft ürününe birden çok Trendyol önerisi düştüğü için atlananlar. */
  skipped: number;
  failed: { name: string; message: string }[];
  /** Süre dolduğu için bu turda işlenmeyen öneri (istemci tekrar çağırır). */
  remaining: number;
}
export const matchingSettingsSchema = z.object({ autoHigh: z.boolean() });
export type MatchingSettings = z.infer<typeof matchingSettingsSchema>;
export const rejectMatchSchema = z.object({ trendyol: z.string().max(40), ideasoft: z.string().max(40), channel: z.enum(CHANNEL_KEYS).optional() });

/** Ortak stok (ana kaynak IdeaSoft). */
export const SHARED_STOCK_MODES = ["off", "suggest", "auto"] as const;
export type SharedStockMode = (typeof SHARED_STOCK_MODES)[number];
export const sharedStockSettingsSchema = z.object({ mode: z.enum(SHARED_STOCK_MODES) });
export interface SharedStockPlanDto {
  ready: boolean;
  reason: string | null;
  linked: number;
  /** key: Yoksay / Geri al için öneri anahtarı (o:varyant, t:kanal kaydı). */
  master: { key: string; name: string; sku: string; stock: number; delta: number; orders: string[] }[];
  /**
   * Kanallar arası farklar. from → to: Trendyol; ideasoftFrom → ideasoftTo: IdeaSoft (null = değişmez).
   * unexplained: Trendyol'da siparişle açıklanmayan satış adedi (ortak havuzdan düşülür).
   * action: trendyol = yalnız Trendyol · ideasoft = havuz (IdeaSoft ve gerekirse Trendyol) ·
   * choose = hangi tarafın değiştiği belli değil; "ideasoft" seçimi IdeaSoft'u Trendyol'daki değere, "trendyol" seçimi Trendyol'u IdeaSoft'a çeker.
   */
  mirror: { key: string; name: string; sku: string; from: number; to: number; ideasoftFrom: number; ideasoftTo: number | null; unexplained: number; held: boolean; review: boolean; action: "trendyol" | "ideasoft" | "choose" }[];
  /** Kullanıcının yoksaydığı öneriler (Geri al ile listeye döner). */
  ignored: { key: string; name: string; sku: string; text: string; at: string }[];
  /** Eşleşmiş ürünler ve iki kanaldaki stok. */
  /** tKey: bu ürünün Trendyol önerisinin anahtarı (Yoksay için). */
  /** trendyolActive: false = Trendyol'da satışa kapalı (IdeaSoft'u düşürmez, başlangıç eşitlemesine girmez). */
  rows: { key: string; tKey: string | null; name: string; brand: string | null; sku: string; ideasoft: number | null; trendyol: number | null; trendyolActive: boolean; status: SharedStockRowStatus; lastAppliedAt: string | null; channels: SharedStockChannelCell[] }[];
  /** Uygulananlar (en yeni önce) ve doğrulama durumu. by: kullanıcı adı, null = otomatik. */
  log: { id: string; at: string; by: string | null; channel: ChannelKey; name: string; sku: string; from: number; to: number; orders: string[]; status: SharedStockLogStatus; checkedAt: string | null; actual: number | null; current: number | null; product: string | null; nowIdeasoft: number | null; nowTrendyol: number | null }[];
  lastCheckAt: string | null;
}
export type SharedStockRowStatus = "equal" | "pending" | "held" | "review" | "ignored" | "inactive" | "sent" | "differs" | "unknown";
/** Eşleşmiş ürünün bir pazar yerindeki stoğu (Trendyol, Hepsiburada…); tKey satırdan uygulama/yoksay anahtarı. */
export interface SharedStockChannelCell {
  key: ChannelKey;
  name: string;
  stock: number | null;
  active: boolean;
  tKey: string;
}
export const SHARED_STOCK_ROW_LABELS: Record<SharedStockRowStatus, string> = {
  equal: "Eşit",
  pending: "Yapılacak",
  held: "Bekletiliyor",
  review: "Seçiminiz bekleniyor",
  ignored: "Yoksayıldı",
  inactive: "Trendyol'da satışa kapalı",
  sent: "Gönderildi, doğrulanacak",
  differs: "Fark var",
  unknown: "Stok bilinmiyor",
};
export type SharedStockLogStatus = "pending" | "verified" | "mismatch" | "unknown";
export const SHARED_STOCK_LOG_LABELS: Record<SharedStockLogStatus, string> = { pending: "Doğrulanacak", verified: "Doğrulandı", mismatch: "Tutmadı", unknown: "Doğrulanamadı" };
export const sharedStockApplySchema = z.object({ key: z.string().regex(/^(o|t):[\w-]{1,64}$/).optional(), action: z.enum(["trendyol", "ideasoft"]).optional() });
export const sharedStockIgnoreSchema = z.object({ key: z.string().regex(/^(o|t):[\w-]{1,64}$/), ignore: z.boolean() });

/** Tüm ürünler → hızlı stok: tek varyantlı ürünün IdeaSoft ve bütün pazar yeri kayıtlarına aynı adet yazılır. */
export const quickStockSchema = z.object({ productId: z.string().min(1).max(64), quantity: z.coerce.number().int().min(0).max(100_000) });
export type QuickStockInput = z.infer<typeof quickStockSchema>;
export interface QuickStockResult {
  quantity: number;
  results: { channel: ChannelKey; name: string; state: "verified" | "accepted" | "failed" | "skipped"; message: string | null }[];
}
export interface SharedStockSettingsDto {
  mode: SharedStockMode;
  enabledAt: string | null;
  lastRunAt: string | null;
  canManage: boolean;
}

/** Denetim kaydı (Ayarlar → Denetim kaydı). */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "auth.login": "Giriş",
  "auth.logout": "Çıkış",
  "auth.password_changed": "Şifre değiştirildi",
  "auth.password_reset": "Şifre sıfırlandı",
  "auth.password_reset_requested": "Şifre sıfırlama istendi",
  "auth.session_revoked": "Oturum kapatıldı",
  "auth.2fa_enabled": "İki aşamalı doğrulama açıldı",
  "auth.2fa_disabled": "İki aşamalı doğrulama kapatıldı",
  "auth.2fa_reset": "İki aşamalı doğrulama sıfırlandı",
  "auth.login_2fa": "Doğrulama kodu girişi",
  "user.avatar_set": "Profil fotoğrafı değişti",
  "user.avatar_removed": "Profil fotoğrafı kaldırıldı",
  "user.created": "Kullanıcı oluşturuldu",
  "user.updated": "Kullanıcı güncellendi",
  "user.password_set": "Kullanıcı şifresi belirlendi",
  "order.status_change": "Sipariş durumu değişti",
  "order.invoice_file": "Fatura dosyası yüklendi",
  "orders.export": "Siparişler dışa aktarıldı",
  "orders.status_map": "Özel durum eşlemesi",
  "listing.update": "Stok / fiyat güncellendi",
  "pricing.bulk.start": "Toplu fiyat başlatıldı",
  "pricing.bulk.finished": "Toplu fiyat tamamlandı",
  "return.approve": "İade onaylandı",
  "return.reject": "İade reddedildi",
  "catalog.merge": "Ürün eşleştirildi",
  "catalog.content.save": "Ürün içeriği kaydedildi",
  "security.policy": "Güvenlik politikası değişti",
  "user.invited": "Kullanıcı davet edildi",
  "user.invitation_revoked": "Davet geri alındı",
  "user.invitation_accepted": "Davet kabul edildi",
  "payments.iyzico.connected": "İyzico bağlandı",
  "payments.iyzico.updated": "İyzico anahtarları değişti",
  "payments.iyzico.removed": "İyzico bağlantısı kaldırıldı",
  "catalog.trendyol.save": "Trendyol taslağı kaydedildi",
  "catalog.trendyol.publish": "Ürün Trendyol'a gönderildi",
  "catalog.trendyol.accept": "Trendyol içeriği panele alındı",
  "catalog.trendyol.settings": "Trendyol yayın ayarları değişti",
  "catalog.trendyol.categories": "Trendyol kategori eşleştirmesi değişti",
  "catalog.trendyol.bulk.start": "Toplu Trendyol gönderimi başladı",
  "catalog.trendyol.bulk.finished": "Toplu Trendyol gönderimi bitti",
  "catalog.content.publish": "Ürün IdeaSoft'a gönderildi",
  "catalog.content.accept": "Kanaldaki içerik panele alındı",
  "catalog.product.create": "Yeni ürün oluşturuldu",
  "catalog.image.add": "Ürün görseli eklendi",
  "catalog.image.remove": "Ürün görseli silindi",
  "catalog.bulk.start": "Toplu ürün düzenleme başladı",
  "catalog.bulk.finished": "Toplu ürün düzenleme bitti",
  "catalog.merge_all": "Tüm öneriler eşleştirildi",
  "catalog.auto_merge": "Otomatik ürün eşleştirme",
  "catalog.settings": "Eşleştirme ayarı",
  "catalog.split": "Ürün ayrıldı",
  "stock.shared.settings": "Ortak stok ayarı",
  "stock.shared.apply": "Ortak stok uygulandı",
  "stock.shared.equalize": "Ortak stok başlangıç eşitlemesi",
  "stock.shared.ignore": "Ortak stok önerisi yoksayıldı",
  "stock.shared.unignore": "Ortak stok önerisi geri alındı",
  "integration.tested": "Bağlantı test edildi",
  "integration.removed": "Entegrasyon kaldırıldı",
  "integration.oauth_started": "IdeaSoft yetkilendirme başladı",
  "integration.oauth_completed": "IdeaSoft yetkilendirildi",
  "integration.history_import": "Geçmiş siparişler aktarılıyor",
  "integration.oauth_failed": "IdeaSoft yetkilendirme başarısız",
  "integration.demo_connected": "Deneme verisi bağlandı",
  "webhook.activate": "Anlık bildirim açıldı",
  "webhook.deactivate": "Anlık bildirim kapatıldı",
  "feedback.create": "Öneri / hata bildirildi",
  "feedback.update": "Öneri güncellendi",
  "feedback.delete": "Öneri silindi",
  "feedback.import_report": "Rapor maddeleri eklendi",
  "onboarding.completed": "Kurulum tamamlandı",
  "customer.viewed": "Müşteri bilgisi görüntülendi",
  "customer.renamed": "Müşteri adı değiştirildi",
  "question.answered": "Müşteri sorusu cevaplandı",
  "question.published": "Soru yayın durumu değişti",
  "review.published": "Yorum yayın durumu değişti",
  "automation.settings": "Otomasyon ayarı",
  "automation.run": "Otomasyon çalıştı",
  "automation.campaign.create": "Kampanya planlandı",
  "automation.campaign.cancel": "Kampanya iptal edildi",
  "automation.campaign.run": "Kampanya fiyatları yazıldı",
  "automation.channel_price": "Kanal fiyat kuralı uygulandı",
};
export const AUDIT_GROUPS: Record<string, { label: string; prefixes: string[] }> = {
  auth: { label: "Giriş ve güvenlik", prefixes: ["auth.", "user.", "bootstrap."] },
  orders: { label: "Siparişler", prefixes: ["order.", "orders.", "return.", "customer.", "question.", "review."] },
  catalog: { label: "Katalog, stok, fiyat", prefixes: ["listing.", "pricing.", "catalog.", "stock."] },
  system: { label: "Entegrasyon ve sistem", prefixes: ["integration.", "webhook.", "onboarding.", "feedback.", "automation."] },
};
export const auditQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(1000).default(1),
  group: z.enum(["auth", "orders", "catalog", "system"]).optional(),
  actor: z.string().max(40).optional(),
  entityId: z.string().max(60).optional(),
  result: z.enum(["success", "failure"]).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;
export interface AuditRowDto {
  id: string;
  createdAt: string;
  action: string;
  actor: { id: string | null; name: string; type: string };
  entityType: string | null;
  entityId: string | null;
  result: "success" | "failure";
  ip: string | null;
  device: string | null;
  before: unknown;
  after: unknown;
}
export interface AuditListResponse {
  items: AuditRowDto[];
  total: number;
  page: number;
  pageSize: number;
  actors: { id: string; name: string }[];
}

/** E-posta bildirim konuları (kullanıcı başına açılır / kapanır). */
export const EMAIL_GROUPS = [
  { key: "orders", label: "Yeni siparişler", description: "Her yeni sipariş için bir e-posta." },
  { key: "cancels", label: "İptal ve iadeler", description: "Sipariş iptali, iade ve yeni iade talebi." },
  { key: "shipping", label: "Kargo süresi uyarıları", description: "Kargoya son teslim süresi dolmak üzere veya geçti." },
  { key: "questions", label: "Müşteri soruları ve yorumlar", description: "Yeni soru (Trendyol, IdeaSoft), Trendyol'da 24 saatlik süre dolmak üzereyken ve 1–2 yıldızlı IdeaSoft yorumu." },
  { key: "guard", label: "Koruma uyarıları", description: "Yanlış fiyat, şüpheli sipariş, bekleyen iade ve azalan çok satan ürün (Otomasyon kuralları açıksa)." },
  { key: "stock", label: "Stok ve ürün uyarıları", description: "Satıştaki ürünün stoğu bitince; ortak stok otomatik uygulamaları ve hataları; Trendyol ürün onayı ve reddi." },
  { key: "system", label: "Sistem uyarıları", description: "Senkron veya zamanlayıcı durursa ve düzelince." },
  { key: "feedback", label: "Öneri ve hata bildirimleri", description: "Ekipten yeni öneri / hata geldiğinde." },
  { key: "digest", label: "Günlük özet", description: "Her sabah 08:00'de dünün sipariş, ciro, iptal ve stok özeti. Yöneticilere varsayılan olarak gider." },
  { key: "weekly", label: "Haftalık rapor", description: "Her pazartesi 08:30'da geçen haftanın sipariş, ciro, en çok satanlar, markalar, iade oranı ve katalog durumu. Yöneticilere varsayılan olarak gider." },
  { key: "releases", label: "Sürüm notları", description: "Panel yeni sürüme geçince yenilikler, nasıl kullanılır ve geri dönüş bilgisi. Yöneticilere varsayılan olarak gider." },
] as const;
export type EmailGroupKey = (typeof EMAIL_GROUPS)[number]["key"];
export const emailPrefsSchema = z.object({ groups: z.partialRecord(z.enum(["orders", "cancels", "shipping", "questions", "guard", "stock", "system", "feedback", "digest", "weekly", "releases"]), z.boolean()) });
export interface EmailSettingsDto {
  configured: boolean;
  /** Gönderen adresin alan adı (tam adres gösterilmez). */
  fromDomain: string | null;
  email: string;
  groups: { key: EmailGroupKey; label: string; description: string; email: boolean }[];
}

/** Geçmiş sipariş aktarımı (IdeaSoft). */
export interface HistoryImportDto {
  oldestOrderAt: string | null;
  orderCount: number;
  job: { status: string; since: string | null; fetched: number; upserted: number; startedAt: string; finishedAt: string | null; error: string | null } | null;
}

/** Ek raporlar: iade analizi, dönem karşılaştırması, ölü stok. */
export const periodQuerySchema = z.object({ from: z.iso.date(), to: z.iso.date(), channel: z.enum(CHANNEL_KEYS).optional() });
export type PeriodQuery = z.infer<typeof periodQuerySchema>;
export interface ReturnsReportResponse {
  totals: { requests: number; approved: number; rejected: number; open: number; returnedUnits: number; soldUnits: number; returnRate: number; refunds: string };
  byProduct: { key: string; label: string; brand: string | null; units: number; requests: number }[];
  byBrand: { key: string; label: string; units: number; soldUnits: number; rate: number }[];
  byReason: { key: string; label: string; requests: number }[];
  byChannel: { key: string; label: string; requests: number; units: number }[];
}
export interface ComparePeriod {
  from: string;
  to: string;
  revenue: string;
  orders: number;
  units: number;
  averageBasket: string;
  refunds: string;
}
export interface CompareReportResponse {
  current: ComparePeriod;
  previous: ComparePeriod;
  lastYear: ComparePeriod;
  brands: { key: string; label: string; current: string; previous: string; lastYear: string }[];
}
export const deadStockQuerySchema = z.object({ days: z.coerce.number().int().min(30).max(365).default(90), channel: z.enum(CHANNEL_KEYS).optional() });
export interface DeadStockResponse {
  days: number;
  totals: { products: number; units: number; value: string };
  items: { productId: string; name: string; brand: string | null; sku: string; stock: number; price: string | null; value: string; lastSoldAt: string | null }[];
}

/** Hesap güvenliği (Ayarlar → Profilim). */
export interface SecurityStatusDto {
  twoFactor: { enabled: boolean; recoveryLeft: number };
  sessions: { id: string; device: string; ip: string | null; createdAt: string; lastSeenAt: string; current: boolean }[];
}

/** Müşteriler (müşteri bilgisi yetkisiyle). */
export const customersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(1000).default(1),
  q: z.string().trim().max(100).optional(),
  channel: z.enum(CHANNEL_KEYS).optional(),
  sort: z.enum(["spent", "orders", "last"]).default("spent"),
  repeat: z.enum(["1"]).optional(),
});
export type CustomersQuery = z.infer<typeof customersQuerySchema>;
export interface CustomerRowDto {
  id: string;
  name: string | null;
  city: string | null;
  channel: { key: ChannelKey; name: string } | null;
  orders: number;
  spent: string;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  returns: number;
}
export interface CustomersResponse {
  items: CustomerRowDto[];
  total: number;
  page: number;
  pageSize: number;
  summary: { customers: number; repeat: number; repeatShare: number };
}
export interface CustomerDetailDto extends CustomerRowDto {
  email: string | null;
  phone: string | null;
  orderList: { id: string; orderNumber: string; orderedAt: string; status: OrderStatusKey; grandTotal: string; itemCount: number; firstItemName: string | null }[];
}

/** Trendyol müşteri soruları. */
export const QNA_STATUSES = ["WAITING_FOR_ANSWER", "WAITING_FOR_APPROVE", "ANSWERED", "REJECTED", "REPORTED", "UNANSWERED"] as const;
export const QNA_STATUS_LABELS: Record<string, string> = {
  WAITING_FOR_ANSWER: "Cevap bekliyor",
  WAITING_FOR_APPROVE: "Onay bekliyor",
  ANSWERED: "Cevaplandı",
  REJECTED: "Cevap reddedildi",
  REPORTED: "Bildirildi",
  UNANSWERED: "Süresi doldu",
};
export const qnaQuerySchema = z.object({
  status: z.enum(QNA_STATUSES).default("WAITING_FOR_ANSWER"),
  page: z.coerce.number().int().min(0).max(500).default(0),
  /** Son kaç gün (Trendyol en fazla 2 haftalık aralık verir). */
  days: z.coerce.number().int().min(1).max(14).default(7),
  channel: z.enum(["trendyol", "ideasoft"]).default("trendyol"),
});
export const qnaChannelSchema = z.object({ channel: z.enum(["trendyol", "ideasoft"]).default("trendyol") });
export const qnaPublishSchema = z.object({ published: z.boolean() });
export const reviewsQuerySchema = z.object({ page: z.coerce.number().int().min(0).max(200).default(0), filter: z.enum(["all", "published", "hidden", "low"]).default("all") });
export const qnaAnswerSchema = z.object({ text: z.string().trim().min(10, "Cevap en az 10 karakter olmalı").max(2000, "Cevap en fazla 2000 karakter") });
export const qnaTemplatesSchema = z.object({ items: z.array(z.object({ id: z.string().max(40), title: z.string().trim().min(1).max(60), text: z.string().trim().min(10).max(2000), keywords: z.string().trim().max(200).optional() })).max(50) });
export interface QnaQuestionDto {
  id: string;
  text: string;
  status: string;
  userName: string | null;
  createdAt: string;
  channel: "trendyol" | "ideasoft";
  /** Trendyol kuralı: 24 saat içinde cevap (IdeaSoft'ta süre yok: null). */
  dueAt: string | null;
  /** IdeaSoft: soru sitede yayında mı. Trendyol: null (Trendyol yönetir). */
  published: boolean | null;
  /** Panel kataloğundaki ürün (varsa). */
  productId: string | null;
  productName: string | null;
  imageUrl: string | null;
  webUrl: string | null;
  answer: { text: string; createdAt: string | null } | null;
  rejectedAnswer: { text: string; reason: string | null } | null;
}
export interface QnaListResponse {
  connected: boolean;
  items: QnaQuestionDto[];
  total: number;
  page: number;
  totalPages: number;
  waiting: number | null;
  /** Kanal başına cevap bekleyen (bağlı değilse null). */
  waitingByChannel: { trendyol: number | null; ideasoft: number | null };
}
export interface ReviewDto {
  id: string;
  title: string;
  content: string;
  rating: number | null;
  published: boolean;
  userName: string | null;
  productName: string | null;
  productId: string | null;
  imageUrl: string | null;
  createdAt: string;
}
export interface ReviewsResponse {
  connected: boolean;
  items: ReviewDto[];
  page: number;
  hasMore: boolean;
}
export interface QnaTemplate {
  id: string;
  title: string;
  text: string;
  /** Cevap önerisi için ek anahtar kelimeler (virgülle). */
  keywords?: string;
}

/** Müşteri adı düzeltme (IdeaSoft'a da yazılır). */
export const customerNameSchema = z.object({
  firstname: z.string().trim().min(1, "Ad girin").max(80),
  surname: z.string().trim().min(1, "Soyad girin").max(80),
});
export interface CustomerNameResultDto {
  state: "verified" | "partial" | "failed";
  name: string;
  member: "updated" | "skipped" | "failed";
  orders: { orderNumber: string; ok: boolean; message?: string }[];
}
